<template>
  <div class="candidate-list-container" :style="isExpendFitScore && expendStyle">
    <el-table
      class="candidate-list"
      row-key="id"
      ref="candidateList"
      tooltip-effect="light"
      :height="isScrollable ? '100%' : null"
      :default-sort="{prop: 'apply_time', order: 'descending'}"
      :data="data"
      :empty-text="$t('el.table.emptyText')"
      :show-header="showHeader"
      @sort-change="handleSortChange"
      @selection-change="handleSelectionChange"
      @select-all="handleSelectAll"
    >
      <el-table-column v-if="!hiddenSelect" width="35px" prop="id" type="selection" class-name="selection" :selectable="(row) => !row.is_agency_uploaded_and_hid"></el-table-column>
      <el-table-column :min-width="tableColumnWidthMap ? tableColumnWidthMap.candidate : '150px'" prop="candidate" :label="$t(`${i18nPrefix}.label.candidateName`)">
        <template #default="{ row }">
          <div
            class="avatar-name-container"
            :class="[row.reviewed || 'is-new', row.candidate.is_employee && 'employee', row.candidate.is_alumni && 'alumni']"
          >
            <el-tooltip
              effect="light"
              popper-class="light-tooltip-popper"
              :content="row.candidate.candidateStatusName"
            >
              <div class="avatar-container">
                <img class="avatar" :src="getCandidateAvatarPath(row)" @click="handleAvatarClick(row)"/>
                <i v-if="row.is_agency_uploaded_and_hid" class="avatar-lock-icon iconfont iconLock"></i>
              </div>
            </el-tooltip>
            <div class="name-container">
              <el-tooltip effect="light" popper-class="light-tooltip-popper" :content="row.candidate.name">
                <div class="name" @click="handleNameClick(row)">{{ row.candidate.name }}</div>
              </el-tooltip>
              <div class="candidate-icon-container">
                <el-tooltip
                  v-if="row.referral_type === REFERRAL_TYPE.EMPLOYEE_SHARED || row.referral_type === REFERRAL_TYPE.CANDIDATE_SHARED"
                  effect="light"
                  popper-class="light-tooltip-popper"
                  placement="bottom"
                  :content="$t(`${i18nPrefix}.label.${row.referral_type === REFERRAL_TYPE.EMPLOYEE_SHARED ? 'employeeReferral' : 'candidateReferral'}`)"
                >
                  <span class="candidate-icon referral">
                    <i class="iconfont iconreferral"></i>
                  </span>
                </el-tooltip>
                <DuplicateCandidatePopover
                  v-if="hasDuplicateCandidatePermission && row.duplicate_candidate_list && row.duplicate_candidate_list.length > 0"
                  class="candidate-icon duplicate-candidate"
                  :duplicateCandidateList="row.duplicate_candidate_list"
                  @openDuplicateCandidateDetail="openDuplicateCandidateDetail"
                >
                  <DuplicateCandidateIcon :size="14"></DuplicateCandidateIcon>
                </DuplicateCandidatePopover>
                <el-tooltip
                  v-if="row.referral_type === REFERRAL_TYPE.EMPLOYEE_SUBMITTED || row.referral_type === REFERRAL_TYPE.CANDIDATE_SUBMITTED"
                  effect="light"
                  popper-class="light-tooltip-popper"
                  placement="bottom"
                  :content="$t(`${i18nPrefix}.label.${row.referral_type === REFERRAL_TYPE.EMPLOYEE_SUBMITTED ? 'employeeSubmitted' : 'candidateSubmitted'}`)"
                >
                  <span  class="candidate-icon submit">
                    <i class="iconfont icona-bianzu53"></i>
                    <i class="iconfont iconxingzhuang dot" :style="dotStyle"></i>
                  </span>
                </el-tooltip>
                <span v-auto-tooltip.always="getCopiedFromJob(row)" v-if="row.copied_info" class="candidate-icon">
                  <div class="is-copy-application">
                    <i class="iconfont icona-bianzu53"></i>
                    <i class="iconfont icona-copybuttonforcoach1 dot"></i>
                  </div>
                </span>
              </div>
            </div>
          </div>
        </template>
      </el-table-column>
      <el-table-column  v-if="!hiddenJob" :min-width="tableColumnWidthMap ? tableColumnWidthMap.job : '120px'" prop="job" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.job`)">
        <template #default="{ row }">{{ getI18nPropertyValue(row.job, 'cn_title', 'title') }}</template>
      </el-table-column>
      <el-table-column :min-width="tableColumnWidthMap ? tableColumnWidthMap.workCompanyName : '110px'" prop="recent_work_experience" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.workCompanyName`)">
        <template #default="{ row }">{{ (row.recent_work_experience && row.recent_work_experience.company)|empty }}</template>
      </el-table-column>
      <el-table-column v-if="!hiddenLastWorkJobName" :min-width="tableColumnWidthMap ? tableColumnWidthMap.lastWorkJobName : '120px'" prop="recent_work_experience" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.lastWorkJobName`)">
        <template #default="{ row }">{{ (row.recent_work_experience && row.recent_work_experience.job)|empty }}</template>
      </el-table-column>
      <el-table-column :min-width="tableColumnWidthMap ? tableColumnWidthMap.maxDegree : '120px'" prop="max_degree" show-overflow-tooltip :sortable="isSortable && 'custom'" :sort-orders="['descending', 'ascending']"  :label="$t(`${i18nPrefix}.label.degree`)">
        <template #default="{ row }">{{ row.candidate.maxDegreeI18nName|empty }}</template>
      </el-table-column>
      <el-table-column :min-width="tableColumnWidthMap ? tableColumnWidthMap.ageAndGender : '75px'" prop="candidate" label-class-name="age-gender-header" :label="$t(`${i18nPrefix}.label.ageAndGender`)">
        <template #default="{ row }">
          <span class="age">{{ row.candidate.age }}</span>
          <span v-if="row.candidate.gender === 'M' || row.candidate.gender === 'F'" class="gender">
            <i
              class="iconfont"
              :class="`iconResume-${row.candidate.gender === 'M' ? 'male' : 'female'}`"
            />
          </span>
        </template>
      </el-table-column>
      <el-table-column :min-width="tableColumnWidthMap ? tableColumnWidthMap.applyTime : '140px'" prop="apply_time" :sortable="isSortable && 'custom'" :sort-orders="['descending', 'ascending']" :label="$t(`${i18nPrefix}.label.appliedDate`)">
        <template #default="{ row }">{{ row.apply_time|formatDate }}</template>
      </el-table-column>
      <el-table-column v-if="isDisabled" :min-width="tableColumnWidthMap ? tableColumnWidthMap.statusName : '130px'" prop="statusName" :label="$t(`${i18nPrefix}.label.status`)">
        <template #default="{ row }">
          <el-button v-auto-tooltip.always="tooltip" disabled class="candidate-status" type="text" @click="handleCandidateStatusClick(row)">{{ row.statusName }}</el-button>
        </template>
      </el-table-column>
      <el-table-column v-else :min-width="tableColumnWidthMap ? tableColumnWidthMap.statusName : '130px'" prop="statusName" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.status`)">
        <template #default="{ row }">
          <el-button class="candidate-status" type="text" :disabled="row.is_agency_uploaded_and_hid || isDisabled" @click="handleCandidateStatusClick(row)">{{ row.statusName }}</el-button>
        </template>
      </el-table-column>
      <el-table-column :min-width="tableColumnWidthMap ? tableColumnWidthMap.channel : '80px'" prop="channel" show-overflow-tooltip :label="$t(`${i18nPrefix}.label.sourceChannel`)" >
        <template #default="{ row }">{{ getI18nPropertyValue(row.channel, 'cn_name', 'name')|empty($t(`${i18nPrefix}.label.weChatChannelName`)) }}</template>
      </el-table-column>
      <el-table-column v-if="hasCRMTagPermission" min-width="160px" prop="tag_list" class-name="tags" :label="$t(`${i18nPrefix}.label.tag`)">
        <template #default="{ row }">
          <CandidateListTagCellVue :tag-list="row.tag_list" />
        </template>
      </el-table-column>
      <el-table-column v-if="showFitScore" width="45px">
        <template #default="{ row }">
          <div class="open-fit-btn" :class="row.candidate.hasFit && 'active'" @click="handleExpendFitScore">%</div>
        </template>
      </el-table-column>
      <el-table-column v-if="operationList" width="45px" prop="id">
        <template #default="{ row }">
          <el-dropdown
            v-if="!row.is_agency_uploaded_and_hid"
            class="operation-btn operation-btn-dropdown"
            trigger="click"
            :class="isDisabled && 'is-disabled'"
            :disabled="isDisabled"
            @command="handleOperation($event, row)"
          >
            <i v-if="tooltip" v-auto-tooltip.always="tooltip" class="btn-icon el-icon-more"></i>
            <i v-else class="btn-icon el-icon-more"></i>
            <template #dropdown>
              <el-dropdown-menu class="no-arrow-dropdown-menu">
                  <el-dropdown-item
                    v-for="(item, index) in operationList"
                    :key="index"
                    :command="item"
                  >
                    <span v-if="!item.childrenMenuList">{{ item.label }}</span>
                    <el-popover
                      v-if="item.childrenMenuList"
                      placement="left"
                      trigger="hover"
                      popper-class="action-children-container"
                    >
                      <div
                        v-for="(child, childIndex) in item.childrenMenuList"
                        v-show="!child.hidden"
                        class="child-item"
                        :key="childIndex"
                        @click="handleOperation(child, row)"
                      >
                        {{ child.label }}
                      </div>
                      <div slot="reference">{{ item.label }}</div>
                    </el-popover>
                  </el-dropdown-item>
                </el-dropdown-menu>
            </template>
          </el-dropdown>
        </template>
      </el-table-column>
    </el-table>
    <div v-if="data?.length" class="fit-score-container" :style="fitScoreContainerStyle" @transitionend="handleFitScoreTransitionEnd">
      <el-table ref="fitScoreTable" class="fit-table" :data="[]" :default-sort="fitScoreDefaultSort" @sort-change="handleFitScoreTableSortChange">
        <el-table-column
          v-for="item in fitScoreHeaderList"
          label-class-name="fit-score-label"
          :min-width="item.width"
          :key="item.label"
          :prop="item.orderField"
          :sortable="item.orderField && isSortable && 'custom'"
          :sort-orders="['descending', 'ascending']"
        >
          <template #header>
            {{ item.label }}
          </template>
        </el-table-column>
      </el-table>
      <div ref="fitScoreBody" class="fit-score-body">
        <template>
          <FitScoreRow
            v-for="(item) in data"
            :key="item.id"
            :personalityFinished="false"
            :problemSolvingFinished="false"
            :cultureFit="item.candidate.organization_fit"
            :personalityFit="item.candidate.job_fit"
            :problemSolvingFit="item.candidate.mental_ability"
            :resumeFitScore="item.ai_match_info?.resume_ai_match_score"
            :aiResumeMatchSummary="getResumeAIMatchSummary(item)"
            :aiInterviewFinished="item.ai_interview_info?.is_finished"
            :aiInterviewFit="item.ai_interview_info?.total_score"
            :aiInterviewScore="item.ai_interview_score"
            :interviewRecommendation="item.hm_interview_recommendation"
            :interviewScore="item.hm_interview_total_rate"
            :completion="item.hm_interview_completion"
            :hmInfoList="item.hm_interview_info_list"
            :showAiInterviewFit="showAiInterviewFit"
            :showResumeFit="hasAIResumeMatchPermission"
            :showAIInterviewScore="showAIInterviewScore"
            :showInterviewScore="showInterviewScore"
          />
        </template>
      </div>
      <div v-if="showFitScore" class="btn-container" @click="handleExpendFitScore">
        <i class="close-fit-btn el-icon-caret-right"/>
      </div>
    </div>
  </div>
</template>

<script>
import _ from 'lodash'
import moment from 'moment'
import { getI18nPropertyValue } from '@/i18n'
import { REFERRAL_TYPE } from '@/constant/application'
import DuplicateCandidatePopover from '@/views/adminNew/application/components/duplicateCandidatePopover.vue'
import DuplicateCandidateIcon from '@/components/icon/duplicateCandidate.vue'
import CandidateListTagCellVue from '@/components/candidate/candidateListTagCell.vue'
import FitScoreRow from '../../jobApplicantList/fitScoreRow.vue'

export default {
  components: {
    DuplicateCandidateIcon,
    DuplicateCandidatePopover,
    CandidateListTagCellVue,
    FitScoreRow
  },
  props: {
    data: {
      type: Array,
      required: true
    },
    operationList: {
      type: Array,
      required: false
    },
    hiddenJob: {
      type: Boolean,
      default: false
    },
    hiddenLastWorkJobName: {
      type: Boolean,
      default: false
    },
    isSortable: {
      type: Boolean,
      default: true
    },
    hiddenSelect: {
      type: Boolean,
      default: false
    },
    showHeader: {
      type: Boolean,
      default: true
    },
    isScrollable: {
      type: Boolean,
      default: true
    },
    tableColumnWidthMap: {
      type: Object,
      required: false
    },
    cancelSelectedCandidateList: {
      type: Array,
      required: true
    },
    isDisabled: {
      type: Boolean,
      default: false
    },
    tooltip: {
      type: String,
      default: null
    },
    showAiInterviewFit: {
      default: false
    },
    showAIInterviewScore: {
      default: false
    },
    showInterviewScore: {
      type: Boolean,
      default: false
    }
  },
  metaInfo () {
    return {
      style: [
        {
          innerHTML: `.el-tooltip__popper.is-light {
            border-color: #e6e6e6;
            background: #e6e6e6;
            -webkit-box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
                    box-shadow: 0px 6px 16px 0px rgba(0, 0, 0, 0.08), 0px 3px 6px -4px rgba(0, 0, 0, 0.12);
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }
          .el-tooltip__popper.is-light[x-placement] .popper__arrow::after {
            border-bottom-color: #e6e6e6;
            border-top-color: #e6e6e6;
          }`
        }
      ]
    }
  },
  data () {
    return {
      i18nPrefix: 'admin.application.applicantList',
      moment,
      getI18nPropertyValue,
      REFERRAL_TYPE,
      atBottom: false,
      emitScrollBottom: null,
      dotColor: '#51bf82',
      size: 14,
      isExpendFitScore: false,
      fitScoreDefaultSort: {
        prop: '',
        order: 'descending'
      }
    }
  },
  computed: {
    showFitScore () {
      if (this.hasAIResumeMatchPermission || this.showAiInterviewFit || this.hasAIInterviewPermission || this.showInterviewScore) {
        return true
      }
      return false
    },
    hasAIResumeMatchPermission () {
      return this.hasAnyPermission(['PREMIUM_AI_RESUME_MATCH', 'PERM_HR_VIEW_AI_FIT_SCORE'])
    },
    hasAIInterviewPermission () {
      return this.hasAnyPermission('PREMIUM_AI_INTERVIEW')
    },
    dotStyle () {
      const dotSize = this.dotSize ? this.dotSize : this.size * 0.6
      const style = { color: this.dotColor }
      if (dotSize >= 12) {
        style.fontSize = `${dotSize}px`
      } else {
        style.fontSize = '12px'
        style.transform = `scale(${dotSize / 12})`
      }
      return style
    },
    hasDuplicateCandidatePermission () {
      return this.hasAnyPermission('PREMIUM_DUPLICATE_CANDIDATE')
    },
    hasCRMTagPermission () {
      return this.hasAnyPermission('PREMIUM_TAG_MANAGEMENT')
    },
    fitScoreContainerRight () {
      return 105 * this.fitScoreHeaderList?.length
    },
    fitScoreContainerStyle () {
      return {
        right: this.isExpendFitScore ? '0px' : `-${this.fitScoreContainerRight + 16}px`,
        width: `${93 * this.fitScoreHeaderList?.length}px`
      }
    },
    expendStyle () {
      return {
        paddingRight: `${93 * this.fitScoreHeaderList?.length - 20}px`
      }
    },
    fitScoreHeaderList () {
      const i18nPrefix = this.i18nPrefix
      const list = [
        {
          hidden: !this.hasAIResumeMatchPermission,
          class: 'resume-fit',
          label: this.$t('admin.application.candidate.title.aiResumeMatchScore'),
          orderField: 'resume_ai_match_score',
          width: '60px'
        },
        {
          hidden: !this.showAiInterviewFit,
          class: 'ai-interview-fit',
          label: this.$t(`${i18nPrefix}.label.aiInterviewFit`),
          orderField: 'ai_interview_fit',
          width: '60px'
        },
        {
          hidden: !this.showAIInterviewScore,
          class: 'ai-interview-score',
          label: this.$t(`${i18nPrefix}.label.aiInterviewScore`),
          orderField: 'ai_interview_score',
          width: '60px'
        },
        {
          hidden: !this.showInterviewScore,
          class: 'interview-score',
          label: this.$t(`${i18nPrefix}.label.interviewRecommendation`),
          orderField: 'latest_interview_result',
          width: '60px'
        }
      ]
      return _.filter(list, item => !item.hidden)
    }
  },
  created () {
    this.emitScrollBottom = _.debounce(() => {
      this.$emit('scrollBottom')
    }, 200)
  },
  mounted () {
    if (this.data?.length) {
      this.$nextTick(() => {
        this.addFitScoreScroll()
      })
    }
  },
  destroyed () {
    window.removeEventListener('resize', this.computeTableSize)
  },
  methods: {
    getCopiedFromJob (row) {
      const copiedInfo = row.copied_info
      const isCopiedFromJob = copiedInfo.is_copied_from_job
      if (isCopiedFromJob) {
        return `${this.$t('admin.application.candidate.label.copiedFrom')}${getI18nPropertyValue(copiedInfo.copied_from_job, 'cn_title', 'title')} - ${copiedInfo.copied_from_job.id}`
      }
      return `${this.$t('admin.application.candidate.label.copiedFrom')}${this.$t('admin.talent.pool.title')}`
    },
    getResumeAIMatchSummary (application) {
      if (!application.ai_match_info) {
        return ''
      }
      return getI18nPropertyValue(application.ai_match_info, 'resume_ai_match_summary_cn', 'resume_ai_match_summary_en')
    },
    addFitScoreScroll () {
      const fitScoreBody = this.$refs.fitScoreBody
      const tableBody = document.querySelector('.el-table__body-wrapper')
      tableBody.addEventListener('scroll', () => {
        if (fitScoreBody) {
          fitScoreBody.scrollTop = tableBody.scrollTop
        }
        const distance = tableBody.scrollHeight - tableBody.scrollTop - tableBody.clientHeight - 10
        if (distance <= 50) {
          if (!this.atBottom) {
            this.emitScrollBottom()
          }
          this.atBottom = true
        } else {
          this.atBottom = false
        }
      })
      if (fitScoreBody) {
        fitScoreBody.addEventListener('scroll', () => {
          tableBody.scrollTop = fitScoreBody.scrollTop
        })
      }
      window.addEventListener('resize', this.computeTableSize)
      this.$nextTick(() => {
        this.computeTableSize()
      })
    },
    refreshSelectAllCandidate () {
      const noSelectedList = _.concat([], this.cancelSelectedCandidateList, _.filter(this.data, item => item.is_agency_uploaded_and_hid))
      this.$refs.candidateList.store.states.selection = _.difference(this.data, noSelectedList)
    },
    handleSortChange ({ prop, order }) {
      if (this.$refs.fitScoreTable) {
        this.$refs.fitScoreTable.clearSort()
      }
      this.fitScoreDefaultSort = { prop: '', order: '' }
      this.$emit('orderChange', prop, order === 'descending' ? '-' : '')
    },
    handleFitScoreTableSortChange ({ prop, order }) {
      this.$refs.candidateList.clearSort()
      this.$emit('orderChange', prop, order === 'descending' ? '-' : '')
      this.fitScoreDefaultSort.prop = prop
      this.fitScoreDefaultSort.order = order
    },
    handleSelectionChange (val) {
      this.$emit('selectedChange', val)
    },
    handleSelectAll (val) {
      this.$emit('selectAll', val)
    },
    handleOperation (operation, row) {
      operation.operator && operation.operator(row)
    },
    handleNameClick (row) {
      this.$emit('openDetail', row)
    },
    handleAvatarClick (row) {
      this.$emit('openDetail', row)
    },
    openDuplicateCandidateDetail (application) {
      this.$emit('openDuplicateCandidateDetail', application)
    },
    handleCandidateStatusClick (row) {
      this.$emit('candidateStatusClick', row)
    },
    getCandidateAvatarPath (row) {
      return row.is_agency_uploaded_and_hid ? this.$DjangoStaticUrl('images_en/default.jpg') : this.$DjangoBaseUrl(row.candidate.photo)
    },
    doLayout () {
      this.$refs.candidateList.doLayout()
    },
    scrollToTop () {
      const el = this.$el.querySelector('.el-table__body-wrapper')
      el.scrollTo({
        top: 0
      })
    },
    handleExpendFitScore () {
      this.isExpendFitScore = !this.isExpendFitScore
    },
    handleFitScoreTransitionEnd () {
      const tableRef = this.$refs.candidateList
      tableRef.layout.updateElsHeight()
      tableRef.layout.updateColumnsWidth()
      this.$nextTick(() => {
        this.computeTableSize()
      })
    },
    resizeFitTableRowHeight () {
      const tableRef = this.$refs.candidateList
      const tableEl = tableRef.$el
      const fitScoreBodyRef = this.$refs.fitScoreBody
      if (tableEl && fitScoreBodyRef) {
        const tableRowList = tableEl.querySelectorAll('.el-table__row')
        const fitScoreRowList = fitScoreBodyRef.querySelectorAll('.fit-score-list')
        _.forEach(tableRowList, (row, index) => {
          if (row) {
            const height = row.clientHeight
            const fitScoreRow = fitScoreRowList[index]
            fitScoreRow.style.height = `${height}px`
            row.style.height = `${height}px`
          }
        })
      }
    },
    computeTableSize () {
      const tableRef = this.$refs.candidateList
      tableRef.layout.updateElsHeight()
      const tableEl = tableRef.$el
      const tableBody = tableEl.querySelector('.el-table__body-wrapper')
      const fitScoreBody = this.$refs.fitScoreBody
      if (fitScoreBody) {
        fitScoreBody.style.height = `${tableBody.clientHeight}px`
      }
      const fitScoreTableEl = this.$refs.fitScoreTable?.$el
      const tableHeader = tableEl.querySelector('.el-table__header-wrapper')
      if (fitScoreTableEl) {
        const fitScoreHeader = fitScoreTableEl.querySelector('.el-table__header-wrapper')
        tableHeader.querySelector('.el-table__cell').style.height = `${fitScoreHeader?.querySelector('.el-table__cell').offsetHeight}px`
        fitScoreHeader.querySelector('.el-table__header').style.height = `${tableHeader.clientHeight}px`
      }

      this.resizeFitTableRowHeight()
    }
  },
  watch: {
    loading: {
      handler () {
        if (this.loading) {
          return
        }
        this.$nextTick(() => {
          const tableEl = this.$refs.candidateList.$el
          const tableBody = tableEl.querySelector('.el-table__body-wrapper')
          const fitScoreBody = this.$refs.fitScoreBody
          if (fitScoreBody) {
            fitScoreBody.style.height = `${tableBody.clientHeight}px`
            tableBody.scrollTop = fitScoreBody.scrollTop
          }
        })
      }
    },
    '$i18n.locale' () {
      const tableRef = this.$refs.candidateList
      tableRef.layout.updateElsHeight()
      tableRef.layout.updateColumnsWidth()
      this.$nextTick(() => {
        this.computeTableSize()
      })
    },
    data () {
      if (this.data?.length) {
        this.$nextTick(() => {
          this.addFitScoreScroll()
        })
      }
    }
  }
}
</script>

<style lang="scss" scoped>
.candidate-list-container {
  position: relative;
  height: 100%;
  display: flex;
  transition: .3s all;
  .candidate-list {
    .avatar-name-container {
      display: flex;
      align-items: center;

      &.is-new {
        .avatar-container {
          &::before {
            content: '';
            display: block;
            width: 5px;
            height: 5px;
            border-radius: 50%;
            background-color: #ee7815;
            position: absolute;
            top: 0;
            left: 0;
          }
        }
      }

      &.employee {
        .avatar-container {
          .avatar {
            border-color: #0096cc;
          }
        }
      }

      &.alumni {
        .avatar-container {
          .avatar {
            border-color: #ee7815;
          }
        }
      }

      .avatar-container {
        position: relative;
        flex-shrink: 0;

        .avatar-lock-icon {
          position: absolute;
          bottom: 2px;
          right: 6px;
          font-size: 14px;
          color: #9e9e9e;
        }

        .avatar {
          display: block;
          width: 44px;
          height: 44px;
          padding: 1px;
          border: 1px solid transparent;
          border-radius: 50%;
          line-height: 1;
          cursor: pointer;
        }
      }

      .name-container {
        flex: 1;
        margin-left: 22px;
        overflow: hidden;

        .name {
          font-size: 15px;
          white-space: nowrap;
          word-break: keep-all;
          overflow: hidden;
          text-overflow: ellipsis;
          cursor: pointer;
        }

        .candidate-icon-container {
          line-height: 16px;

          .candidate-icon {
            cursor: pointer;

            & + .candidate-icon {
              margin-left: 5px;
            }

            &.submit, &.referral {
              .iconfont {
                font-size: 14px;
              }
            }

            &.submit {
              position: relative;

              .dot {
                border-radius: 50%;
                background: #fff;
                position: absolute;
                right: -3px;
                bottom: -3px;
                z-index: 1;
              }
            }
          }
          .is-copy-application {
            position: relative;
            line-height: 1;
            display: inline-block;
            .iconfont {
              font-size: 14px;
            }

            .dot {
              font-size: 12px;
              transform: scale(0.7);
              color: #ee7815;
              border-radius: 50%;
              background: #fff;
              position: absolute;
              right: -3px;
              bottom: -3px;
              z-index: 1;
            }
          }
        }
      }
    }

    .gender {
      margin-left: 2px;
    }

    .candidate-status {
      width: 100%;
      padding: 5px 0;
      border: 0;
      text-align: left;
      overflow: hidden;
      text-overflow: ellipsis;
    }

    .operation-btn {
      cursor: pointer;

      .btn-icon {
        transform: rotateZ(90deg);
      }

      &.is-disabled {
        cursor: not-allowed;
      }
    }

    .open-fit-btn {
      width: 30px;
      height: 30px;
      line-height: 30px;
      background-color: #f3f3f3;
      text-align: center;
      color: #606266;
      border-radius: 50%;
      cursor: pointer;

      &.active {
        background: #0096cc;
        color: #fff;
      }

      &:hover {
        background: #008fc3;;
        color: #fff;
      }
    }

    ::v-deep {
      .cell {
        line-height: 18px;
      }

      .el-checkbox__inner {
        width: 18px;
        height: 18px;

        &::after {
          height: 9px;
          left: 6px;
          top: 2px;
        }
      }

      .selection {
        .cell {
          padding: 0;
          text-align: center;
        }
      }

      .age-gender-header {
        word-break: break-word;
      }
    }
  }

  .fit-score-container {
    height: 100%;
    background-color: #fff;
    box-shadow: 0 0 10px rgba(0, 0, 0, .12);
    transition: .3s all;
    position: absolute;
    top: 0;
    right: 0;
    z-index: 1;

    .fit-table {
      ::v-deep .fit-score-label {
        word-break: break-word;
        text-align: center;
        font-size: 12px;
        line-height: 16px;
      }

      ::v-deep .el-table__body-wrapper {
        display: none;
      }

      ::v-deep {
        .is-sortable.descending {
          .caret-wrapper {
            .sort-caret.descending {
              border-top-color: #0096cc;
            }
          }
        }

        .is-sortable.ascending {
          .caret-wrapper {
            .sort-caret.ascending {
              border-bottom-color: #0096cc;
            }
          }
        }
      }
    }

    .fit-score-body {
      overflow: auto;
    }

    .btn-container {
      position: absolute;
      top: 125px;
      left: -16px;
      width: 16px;
      height: 35px;
      line-height: 35px;
      background: #fff;
      cursor: pointer;
      font-size: 12px;
      box-shadow: -2px 0px 10px rgba(0, 0, 0, .12);

      .close-fit-btn {
        margin-left: 2px;
      }
    }
  }
}

.action-children-container {
  padding: 10px 0;

  .child-item {
    line-height: 36px;
    padding: 0 20px;
    margin: 0;
    cursor: pointer;

    &:hover {
      color: #0096cc;
      background-color: #ecf5ff;
    }
  }
}
</style>
